import { ReactNode } from 'react';
import { Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * A small ⓘ beside a term a vendor may not know — "keyword difficulty",
 * "traffic value", "referring domains" — that says what it means in plain
 * words on hover or focus. The explanation is the child; `label` names the
 * button for screen readers ("What is keyword difficulty?").
 */
export function InfoTip({ children, label, className }: { children: ReactNode; label: string; className?: string }) {
  return (
    // Its own provider, so it works in tests and portals without the app's.
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
              className,
            )}
          >
            <Info className="size-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px] leading-relaxed">{children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** A label with its explanation: "Difficulty ⓘ". */
export function TermLabel({ children, tip, label, className }: { children: ReactNode; tip: ReactNode; label: string; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      {children}
      <InfoTip label={label}>{tip}</InfoTip>
    </span>
  );
}
